import { ClaimEmailWithRecipients, EmailDirection } from "@/api/generated/client/models";
import { format } from "date-fns";
import { AlertCircle, Inbox, Send, Reply, Trash2 } from "lucide-react";
import DOMPurify from "dompurify";
import { useState } from "react";

interface EmailListProps {
    emails: ClaimEmailWithRecipients[];
    /** Total emails on the claim across all pages; used to flag truncation. */
    total?: number;
    onSelectEmail: (email: ClaimEmailWithRecipients) => void;
    onReplyEmail?: (email: ClaimEmailWithRecipients) => void;
    /** When provided, each row shows a dismiss (remove from list) button. */
    onDeleteEmail?: (email: ClaimEmailWithRecipients) => void;
    /**
     * When true (default) the list caps its height at 400px and scrolls
     * internally — suited to the narrow right-sidebar module. Set to false in
     * the roomier center Email panel so the list grows to fit and the panel's
     * own ScrollArea handles overflow (avoids a cramped nested scrollbar).
     */
    scrollable?: boolean;
}

function htmlToPlainText(html?: string | null): string {
    if (!html) return "";
    return html
        .replace(/<style[\s\S]*?<\/style>/gi, " ")
        .replace(/<script[\s\S]*?<\/script>/gi, " ")
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

export function EmailList({ emails, total, onSelectEmail, onReplyEmail, onDeleteEmail, scrollable = true }: EmailListProps) {
    const [collapsedMessages, setCollapsedMessages] = useState<Record<number, boolean>>({});

    const toggleMessageCollapsed = (emailId: number) => {
        setCollapsedMessages((prev) => {
            const current = prev[emailId] ?? true;
            return {
                ...prev,
                [emailId]: !current,
            };
        });
    };

    if (emails.length === 0) {
        return (
            <div className="text-center py-6 text-sm text-muted-foreground">
                No emails yet
            </div>
        );
    }

    // When the claim has more emails than we fetched, tell the user instead of
    // silently hiding the remainder.
    const hasMore = typeof total === "number" && total > emails.length;

    return (
        <>
        <div className={`space-y-2 pt-2 ${scrollable ? "max-h-[400px] overflow-y-auto" : ""} overflow-x-hidden pr-1 scrollbar-thin scrollbar-thumb-muted-foreground/20 hover:scrollbar-thumb-muted-foreground/40 w-0 min-w-full`}>
            {emails.map((email) => {
                const toRecipients = email.recipients
                    .filter((r) => r.recipientType === "to")
                    .map((r) => r.email)
                    .join(", ");

                // Determine if this is a received email (inbound)
                const isReceived = email.direction === EmailDirection.Inbound;
                const displayIcon = isReceived
                    ? <Inbox className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                    : <Send className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />;
                const messageText = (email.bodyText || htmlToPlainText(email.bodyHtml) || "").trim();
                const hasMessageContent = Boolean(email.bodyHtml || messageText);
                const isMessageCollapsed = collapsedMessages[email.id] ?? true;

                return (
                    <div
                        key={email.id}
                        className={`text-xs bg-card border rounded-md p-3 cursor-pointer hover:bg-muted/50 transition-colors group relative w-full min-w-0 overflow-hidden ${isReceived ? 'border-l-4 border-l-blue-500 dark:border-l-blue-400' : ''}`}
                        onClick={() => onSelectEmail(email)}
                    >
                        <div className="flex justify-between items-center min-w-0">
                            <p className="text-[11px] text-muted-foreground flex-1 min-w-0 break-words" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                                {isReceived ? `From: ${email.fromEmail}` : `To: ${toRecipients}`}
                            </p>
                            <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                <span className="shrink-0">{displayIcon}</span>
                                <p className="text-[10px] text-muted-foreground">
                                    {email.sentAt
                                        ? format(new Date(email.sentAt), "MMM d, h:mm a")
                                        : format(new Date(email.createdAt), "MMM d, h:mm a")}
                                </p>
                                {onDeleteEmail && (
                                    <button
                                        type="button"
                                        aria-label="Dismiss email"
                                        title="Remove from list"
                                        className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus:opacity-100"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteEmail(email);
                                        }}
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                )}
                            </div>
                        </div>
                        <p className="mt-1 font-medium block min-w-0 break-words" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                            {email.subject || "(No Subject)"}
                        </p>
                        {hasMessageContent && isMessageCollapsed && (
                            <button
                                type="button"
                                className="mt-1 text-[11px] text-primary hover:underline"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMessageCollapsed(email.id);
                                }}
                            >
                                Show message
                            </button>
                        )}
                        {hasMessageContent && !isMessageCollapsed && (
                            <div
                                className="mt-2 w-full min-w-0 max-w-full overflow-x-hidden rounded border bg-muted/20 p-2"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {email.bodyHtml ? (
                                    <div
                                        className="prose prose-sm dark:prose-invert max-w-none text-[11px] text-foreground/90 break-words [&_*]:max-w-full [&_*]:min-w-0 [&_img]:max-w-full [&_table]:w-full [&_table]:max-w-full [&_table]:table-fixed [&_td]:break-words [&_th]:break-words [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_a]:break-all [&_a]:whitespace-normal [&_a]:pointer-events-none"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.bodyHtml) }}
                                    />
                                ) : (
                                    <p
                                        className="text-[11px] text-foreground/90 whitespace-pre-wrap break-words"
                                        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                                    >
                                        {messageText}
                                    </p>
                                )}
                            </div>
                        )}
                        {hasMessageContent && !isMessageCollapsed && (
                            <button
                                type="button"
                                className="mt-1 text-[11px] text-primary hover:underline"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMessageCollapsed(email.id);
                                }}
                            >
                                Hide message
                            </button>
                        )}
                        {isReceived && onReplyEmail && (
                            <div className="mt-2 flex justify-start">
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] text-primary hover:bg-primary/5"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onReplyEmail(email);
                                    }}
                                >
                                    <Reply className="h-3 w-3" />
                                    Reply
                                </button>
                            </div>
                        )}
                        {email.status === "failed" && email.errorMessage && (
                            <div className="mt-2 flex items-start gap-1 text-[10px] text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 p-1.5 rounded border border-red-200 dark:border-red-800">
                                <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                                <span className="break-words">{email.errorMessage}</span>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
        {hasMore && (
            <p className="pt-2 text-center text-[11px] text-muted-foreground">
                Showing {emails.length} of {total} emails
            </p>
        )}
        </>
    );
}
